<template>
  <svg
    :id="id"
    ref="svgElement"
    class="chart-legend"
    :width="totalWidth"
    :height="totalHeight"
  >
    <defs>
      <radar-chart-patterns :id="id" :u="0.55"></radar-chart-patterns>
      <bar-chart-patterns :id="id" :bar-width="15"></bar-chart-patterns>

      <pattern
        id="legend-circle"
        x="0"
        y="0"
        width="15"
        height="20"
        patternUnits="userSpaceOnUse"
      >
        <use
          width="70"
          height="70"
          x="0"
          y="5"
          fill="#fff"
          style="color: #fff"
          href="#svg-symbols-circle-filled"
          transform="scale(0.225)"
        />
        <use
          width="70"
          height="70"
          x="0"
          y="5"
          stroke="blue"
          fill="#fff"
          href="#svg-symbols-circle"
          transform="scale(0.225)"
        />
      </pattern>
    </defs>

    <g
      v-for="(entries, groupName) in filteredGroups"
      :key="groupName"
      :transform="`translate(0, ${yPositions[groupName].y})`"
    >
      <g
        v-for="(entry, index) in entries"
        :key="entry.type + entry.id"
        :transform="`translate(0, ${yPositions[groupName].entries[index]})`"
        @dblclick="openShortTitlePopup(entry)"
      >
        <text
          v-if="
            (settings.xAxisLabelsShortened && entry.isColumn) ||
            (settings.groupLabelsShortened && entry.isItem)
          "
          class="chart-legend__shorttitle"
          :x="
            entry.color || entry.colors || entry.symbol || entry.pattern
              ? frontPadding
              : 0
          "
          :y="(entry.patternHeight ?? 12) / 2 + 6"
          @click="openShortTitlePopup(entry)"
        >
          {{ entry.shortTitle }}:
        </text>
        <text
          :x="
            (entry.color || entry.colors || entry.symbol || entry.pattern
              ? frontPadding
              : 0) +
            ((settings.xAxisLabelsShortened && entry.isColumn) ||
            (settings.groupLabelsShortened && entry.isItem)
              ? longestShortTitleWidth + 8
              : 0)
          "
          :y="(entry.patternHeight ?? 12) / 2 + 6"
        >
          {{ entry.title }}
        </text>

        <use
          v-if="entry.symbol"
          :href="`#svg-symbols-${entry.symbol}-filled`"
          x="0"
          y="0"
          width="70"
          height="70"
          transform="scale(0.175)"
        />

        <template v-if="entry.color || entry.pattern">
          <rect
            v-if="entry.color"
            :width="entry.patternWidth ?? 15"
            :height="entry.patternHeight ?? 12"
            :fill="entry.color || 'transparent'"
          ></rect>
          <rect
            v-if="entry.pattern"
            :width="entry.patternWidth ?? 15"
            :height="entry.patternHeight ?? 12"
            :fill="entry.pattern"
          ></rect>
        </template>
        <template v-else-if="entry.colors && entry.colors.length > 0">
          <line x2="15" y1="7" y2="7" stroke="var(--color-text)"></line>
          <g
            v-for="(color, j) in entry.colors"
            :key="color.title"
            :transform="`translate(20, ${(j + 1) * 16} )`"
          >
            <rect
              v-if="color.symbol == null"
              :width="13"
              :height="13"
              :fill="color.color || 'transparent'"
            ></rect>
            <use
              v-else
              :href="`#svg-symbols-${color.symbol}-filled`"
              x="0"
              y="0"
              width="70"
              height="70"
              transform="scale(0.175)"
              :style="{ color: color.color || undefined }"
            />
            <text
              x="20"
              y="10"
              style="font-size: 12px"
              :fill="color.color || undefined"
            >
              {{ color.title }}
            </text>
          </g>
        </template>
      </g>
    </g>
  </svg>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useSVGTextWidthCache } from "@core/composables/useSVGTextWidthCache";
import { v4 } from "uuid";

import type { LegendEntry } from "@editor/items/charts/ChartData";
import type { ChartSettings } from "@editor/items/charts/ChartItem";

import RadarChartPatterns from "@editor/items/charts/RadarChartPatterns.vue";
import BarChartPatterns from "@editor/items/charts/BarChartPatterns.vue";

const props = withDefaults(
  defineProps<{
    legend: { [groupName: string]: LegendEntry[] };
    id?: string;
    settings?: Partial<ChartSettings>;
  }>(),
  {
    id: () => v4(),
    settings: () => ({})
  }
);

const emit = defineEmits<{
  (
    e: "update:short-title",
    data: {
      uid: string;
      title: string;
      shortTitle?: string;
      type: string;
      id: string | number;
    }
  ): void;
}>();

const svgElement = ref<SVGSVGElement>();
const { measureTextWidthCached } = useSVGTextWidthCache(svgElement);

// ==========
// Grouping

const filteredGroups = computed(() => {
  const groups = { ...props.legend };

  for (const groupName in props.legend) {
    const entries = groups[groupName];
    groups[groupName] = entries.filter((entry) => {
      if (
        !props.settings.xAxisLabelsShortened &&
        !props.settings.groupLabelsShortened &&
        !entry.pattern &&
        !entry.symbol &&
        !entry.color &&
        (!entry.colors || entry.colors.length == 0)
      )
        return false;
      return true;
    });
  }

  return groups;
});

const groups = computed(() => {
  return Object.values(filteredGroups.value);
});

// ===========
// Y positions

const yPositions = computed(() => {
  const positions: Record<string, { y: number; entries: number[] }> = {};

  const LINE_HEIGHT = 20;
  const GROUP_GAP = 5;

  let nextLinePosition = 5;
  for (const groupName in filteredGroups.value) {
    const groupPosition = nextLinePosition;
    positions[groupName] = { y: groupPosition, entries: [] };

    for (const entry of filteredGroups.value[groupName]) {
      positions[groupName].entries.push(nextLinePosition - groupPosition);
      let height = (entry.patternHeight ?? 0) + 5;
      nextLinePosition += Math.max(LINE_HEIGHT, height);
      if (entry.color == null && entry.colors && entry.colors.length >= 1) {
        nextLinePosition += (entry.colors || []).length * LINE_HEIGHT;
      }
    }
    nextLinePosition += GROUP_GAP;
  }
  positions["end"] = { y: nextLinePosition, entries: [] };

  return positions;
});

// =================
// Front dimensions
// (icon spacing)

const hasSymbols = computed(() => {
  return groups.value.some((group) =>
    group.some((entry) => entry.symbol != null)
  );
});

const hasColors = computed(() => {
  return groups.value.some((group) =>
    group.some(
      (entry) =>
        entry.color != null ||
        entry.pattern != null ||
        (entry.colors != null && entry.colors.length > 0)
    )
  );
});

const maxColorWidth = computed(() => {
  const STANDARD_COLOR_WIDTH = 15;

  return groups.value.reduce((size, group) => {
    return Math.max(
      size,
      group.reduce((size, entry) => {
        if (entry.patternWidth != null) return entry.patternWidth;
        return size;
      }, STANDARD_COLOR_WIDTH)
    );
  }, STANDARD_COLOR_WIDTH);
});

const frontPadding = computed(() => {
  return hasColors.value || hasSymbols.value ? maxColorWidth.value + 7 : 0;
});

// ================
// TEXT WIDTHS

const longestTextWidth = computed(() => {
  return groups.value.reduce(
    (previousValue, legendGroup) =>
      Math.max(
        legendGroup.reduce(
          (previousValue, entry) =>
            Math.max(measureTextWidthCached(entry.title, 14), previousValue),
          0
        ),
        previousValue
      ),
    0
  );
});

const longestShortTitleWidth = computed(() => {
  if (
    props.settings.groupLabelsShortened == false &&
    props.settings.xAxisLabelsShortened == false
  )
    return 0;

  const w = groups.value.reduce(
    (previousValue, legendGroup) =>
      Math.max(
        legendGroup.reduce(
          (previousValue, entry) =>
            Math.max(
              measureTextWidthCached(entry.shortTitle || "", 14) * 1.1,
              previousValue
            ),
          0
        ),
        previousValue
      ),
    0
  );

  return w;
});

// ==================
// Summed dimensions

const totalWidth = computed(() => {
  return longestTextWidth.value + longestShortTitleWidth.value + 50;
});

const totalHeight = computed(() => {
  return yPositions.value.end.y;
});

// =======
// METHODS

function openShortTitlePopup(entry: LegendEntry) {
  if (!props.settings.xAxisLabelsShortened && !props.settings.groupLabelsShortened)
    return;

  emit("update:short-title", {
    uid: `${entry.type}_${entry.id}`,
    title: entry.title,
    shortTitle: entry.shortTitle,
    type: entry.type,
    id: entry.id
  });
}

defineExpose({
  totalWidth,
  totalHeight
});
</script>

<style lang="scss">
.chart-legend {
  max-width: 100%;
  min-width: 0;
  fill: currentColor;
  font-size: 14px;

  &__shorttitle {
    font-weight: 700;
    font-size: 13px;
    cursor: pointer;
    fill: var(--color-text-medium);
    &:hover {
      fill: var(--color-text);
    }
  }
}
</style>
